/**
 * The session rules, as pure functions — the `auth.*` counterpart to
 * onboarding.ts, and shared for the same reason.
 *
 * Two decisions live here rather than in either host, because a widget author
 * builds against the mock and ships against the bridge, and a divergence in
 * either one is a widget that renders "you're in" to someone who is not:
 *
 * 1. **What `authenticated` means.** A platform id or nothing. Both hosts derive
 *    the answer with `widgetAuthStatus`, so neither can decide that an anonymous
 *    onboarding visitor is "sort of" signed in.
 * 2. **What a submit's `onSuccess` produces.** The follow-up is normalised and
 *    the two result flags are computed here (`submitFollowUpOutcome`), so the
 *    mock cannot report a different `{ signedIn, redirected }` pair than the real
 *    host for the same situation.
 *
 * What is *not* here: anything that performs a sign-in, a redirect or a logout.
 * Those are host mechanics by definition — a browser host replaces the document,
 * a native shell may not — and only the rules around them are shared.
 */

import {
  OnboardingBusinessFollowUp,
  OnboardingSubmitFollowUp,
  WidgetAuthStatus,
} from './contracts';

/** What a host knows about the current customer, before it becomes a status. */
export interface WidgetAuthIdentity {
  /** The platform user id, when there is a signed-in customer. */
  userId?: string;
  /** The customer's active platform role, when there is one. */
  activeRole?: string;
}

/**
 * The one derivation of `auth.status`.
 *
 * A user id is the whole test: the platform has no anonymous session, so an id
 * means a session and no id means none. Deliberately *not* derived from the
 * page's role (`context.page.role` / `context.user.roles`), which is a URL
 * segment an anonymous visitor also has — reading that as authentication is
 * exactly the bug this function exists to make unwritable.
 *
 * `userId` and `roles` are omitted rather than emptied when there is no session:
 * `{ authenticated: false, userId: '' }` invites `if (status.userId)` to be
 * written as the session check, and then a blank id and a real one differ only by
 * truthiness.
 */
export function widgetAuthStatus(identity: WidgetAuthIdentity | undefined): WidgetAuthStatus {
  const userId = (identity?.userId ?? '').trim();
  if (!userId) return { authenticated: false };
  const status: WidgetAuthStatus = { authenticated: true, userId };
  const activeRole = (identity?.activeRole ?? '').trim();
  if (activeRole) status.roles = [activeRole];
  return status;
}

/**
 * Normalises the individual submit's `onSuccess`. Anything unrecognised — a
 * typo, a value from a future SDK — becomes 'stay', which is the only safe
 * default: it navigates nowhere and signs nobody in, so a misspelt follow-up
 * costs a widget its redirect rather than stranding a customer somewhere.
 */
export function normaliseSubmitFollowUp(raw: unknown): OnboardingSubmitFollowUp {
  return raw === 'signIn' || raw === 'goToLogin' ? raw : 'stay';
}

/**
 * The message both hosts reject a business 'signIn' with. `POST
 * /organisations/onboarding` returns `{ id, primaryUserId }` and mints no
 * tokens, so there is nothing to consume; downgrading it silently to
 * 'goToLogin' would teach a widget author that the parity exists.
 */
export const BUSINESS_SIGN_IN_UNSUPPORTED_MESSAGE =
  "onboarding.submitBusiness does not support onSuccess 'signIn': the business onboarding endpoint " +
  "returns no session. Use 'goToLogin' and let the primary member sign in.";

/**
 * Normalises the business submit's `onSuccess`. 'signIn' is not narrowed away
 * quietly — the caller is told, via `unsupported`, so it can raise the shared
 * message above as a host error before anything is sent.
 */
export function normaliseBusinessFollowUp(raw: unknown): {
  followUp: OnboardingBusinessFollowUp;
  unsupported: boolean;
} {
  if (raw === 'signIn') return { followUp: 'stay', unsupported: true };
  return { followUp: raw === 'goToLogin' ? 'goToLogin' : 'stay', unsupported: false };
}

/**
 * The `{ signedIn, redirected }` pair a follow-up produces.
 *
 * `hasSession` is whether the platform actually returned auto-login tokens —
 * best-effort by the platform's own design, so 'signIn' without a session is a
 * normal case and not an error: the host falls back to the login page, which is
 * a redirect without a sign-in. That is the whole reason these are two booleans
 * and not one enum: a widget renders "you're in" from the first and "we're taking
 * you to sign in" from the second, and the fallback is honestly both.
 */
export function submitFollowUpOutcome(
  followUp: OnboardingSubmitFollowUp,
  hasSession: boolean,
): { signedIn: boolean; redirected: boolean } {
  if (followUp === 'signIn') {
    return hasSession ? { signedIn: true, redirected: true } : { signedIn: false, redirected: true };
  }
  if (followUp === 'goToLogin') return { signedIn: false, redirected: true };
  return { signedIn: false, redirected: false };
}
